import React, { useState } from 'react';
import { Button, Group, Popover, Stack, Text } from '@mantine/core';
import { PermissionRestrictedTooltip } from '../../../permissions/scheduler';

export interface TrackTaskConfirmControlProps {
  disabled?: boolean;
  loading?: boolean;
  canMutate?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * "Track task" CTA with a one-line explanation + confirm before converting to WBS.
 */
export function TrackTaskConfirmControl({
  disabled = false,
  loading = false,
  canMutate = true,
  onConfirm,
}: TrackTaskConfirmControlProps) {
  const [opened, setOpened] = useState(false);
  const restricted = !canMutate;

  return (
    <PermissionRestrictedTooltip restricted={restricted}>
      <Popover
        opened={opened}
        onChange={setOpened}
        width={280}
        position="bottom-start"
        withArrow
        shadow="md"
        radius="md"
        withinPortal
        zIndex={500}
        disabled={restricted || disabled || loading}
      >
        <Popover.Target>
          <Button
            size="xs"
            variant="light"
            loading={loading}
            disabled={disabled || restricted}
            style={{ flexShrink: 0 }}
            onClick={() => {
              if (restricted || disabled || loading) return;
              setOpened((v) => !v);
            }}
          >
            Track task
          </Button>
        </Popover.Target>
        <Popover.Dropdown p="sm">
          <Stack gap="sm">
            <Text size="xs" style={{ lineHeight: 1.4 }}>
              This converts the task into a WBS activity so site progress can be tracked against
              it.
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button
                size="xs"
                variant="default"
                disabled={loading}
                onClick={() => setOpened(false)}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="brand"
                loading={loading}
                disabled={loading}
                onClick={() => {
                  setOpened(false);
                  void onConfirm();
                }}
              >
                Track task
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </PermissionRestrictedTooltip>
  );
}
